import React, { useState, useEffect, useCallback } from "react";
import {
  DollarSign, Tag, User, SlidersHorizontal, Plus, UploadCloud, EyeOff,
  CheckCircle2, Clock, AlertCircle, Image as ImageIcon, FileText,
  StickyNote, Archive, Trash2, Copy, Loader2, ArrowRight, History,
} from "lucide-react";
import { getPropertyActivityLog } from "../../../api/propertyManagement";
import { formatMelbourneDateTime } from "../../../utils/melbourneTime";

// action -> { icon, label, colour classes }
const ACTION_CONFIG = {
  created:            { icon: Plus,             color: "text-green-600 bg-green-50" },
  updated:            { icon: SlidersHorizontal, color: "text-slate-600 bg-slate-100" },
  price_changed:      { icon: DollarSign,       color: "text-emerald-700 bg-emerald-50" },
  status_changed:     { icon: Tag,              color: "text-amber-700 bg-amber-50" },
  agent_changed:      { icon: User,             color: "text-blue-700 bg-blue-50" },
  published_rea:      { icon: UploadCloud,      color: "text-sky-700 bg-sky-50" },
  published_domain:   { icon: UploadCloud,      color: "text-sky-700 bg-sky-50" },
  published_website:  { icon: UploadCloud,      color: "text-sky-700 bg-sky-50" },
  unpublished_rea:    { icon: EyeOff,           color: "text-gray-600 bg-gray-100" },
  unpublished_domain: { icon: EyeOff,           color: "text-gray-600 bg-gray-100" },
  unpublished_website:{ icon: EyeOff,           color: "text-gray-600 bg-gray-100" },
  rea_resync_success: { icon: CheckCircle2,     color: "text-green-700 bg-green-50" },
  rea_resync_pending: { icon: Clock,            color: "text-amber-700 bg-amber-50" },
  rea_resync_failed:  { icon: AlertCircle,      color: "text-red-700 bg-red-50" },
  photo_added:        { icon: ImageIcon,        color: "text-indigo-600 bg-indigo-50" },
  photo_removed:      { icon: ImageIcon,        color: "text-gray-600 bg-gray-100" },
  document_added:     { icon: FileText,         color: "text-indigo-600 bg-indigo-50" },
  document_removed:   { icon: FileText,         color: "text-gray-600 bg-gray-100" },
  note_added:         { icon: StickyNote,       color: "text-yellow-700 bg-yellow-50" },
  duplicated:         { icon: Copy,             color: "text-slate-600 bg-slate-100" },
  archived:           { icon: Archive,          color: "text-gray-600 bg-gray-100" },
  withdrawn:          { icon: Archive,          color: "text-gray-600 bg-gray-100" },
  deleted:            { icon: Trash2,           color: "text-red-700 bg-red-50" },
};

function relativeTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  // Beyond a week, fall back to the Melbourne absolute date.
  return absoluteTime(dateStr);
}

// Absolute timestamp shown in agency (Melbourne) time, not viewer-local.
function absoluteTime(dateStr) {
  return formatMelbourneDateTime(dateStr);
}

function formatValue(label, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (value === true || value === "True") return "Yes";
  if (value === false || value === "False") return "No";
  const str = String(value);
  const isPriceField = /price|rent|amount|commission|fee/i.test(label || "");
  if (isPriceField && /^\d+(\.\d+)?$/.test(str)) {
    return new Intl.NumberFormat("en-AU", {
      style: "currency", currency: "AUD", maximumFractionDigits: 0,
    }).format(Number(str));
  }
  return str;
}

export default function PropertyActivityLog({ propertyId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getPropertyActivityLog(propertyId);
      setLogs(res?.data?.results || []);
    } catch (e) {
      setError("Couldn't load the activity log. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading activity…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertCircle className="w-8 h-8 text-red-400 mb-3" />
        <p className="text-sm text-gray-600 mb-4">{error}</p>
        <button
          onClick={load}
          className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-gray-300 hover:bg-gray-50"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!logs.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <History className="w-8 h-8 text-gray-300 mb-3" />
        <p className="text-sm text-gray-500">No activity recorded yet.</p>
        <p className="text-xs text-gray-400 mt-1">
          Changes to this property will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in duration-500 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-[#374151]">Activity Log</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {logs.length} {logs.length === 1 ? "entry" : "entries"} · newest first
          </p>
        </div>
        <button
          onClick={load}
          className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 border border-gray-200 hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      <ol className="relative border-l-2 border-gray-100 ml-3">
        {logs.map((log) => {
          const cfg = ACTION_CONFIG[log.action] || ACTION_CONFIG.updated;
          const Icon = cfg.icon;
          const changes = log.changes && typeof log.changes === "object"
            ? Object.entries(log.changes)
            : [];
          return (
            <li key={log.id} className="mb-7 ml-6">
              <span
                className={`absolute -left-[15px] flex items-center justify-center w-7 h-7 rounded-full ring-4 ring-white ${cfg.color}`}
              >
                <Icon className="w-3.5 h-3.5" />
              </span>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#374151]">
                    {log.action_display || log.action}
                  </p>
                  {log.description && (
                    <p className="text-xs text-gray-500 mt-0.5 break-words">
                      {log.description}
                    </p>
                  )}
                </div>
                <span
                  className="text-[11px] text-gray-400 whitespace-nowrap shrink-0"
                  title={absoluteTime(log.created_at)}
                >
                  {relativeTime(log.created_at)}
                </span>
              </div>

              {changes.length > 0 && (
                <div className="mt-2 space-y-1 bg-gray-50 border border-gray-100 rounded p-3">
                  {changes.map(([field, delta]) => {
                    const oldV = delta && typeof delta === "object" ? delta.old : undefined;
                    const newV = delta && typeof delta === "object" ? delta.new : delta;
                    return (
                      <div key={field} className="flex items-center flex-wrap gap-1.5 text-xs">
                        <span className="font-semibold text-gray-600">{field}:</span>
                        <span className="text-gray-500 line-through decoration-gray-300">
                          {formatValue(field, oldV)}
                        </span>
                        <ArrowRight className="w-3 h-3 text-gray-400" />
                        <span className="font-medium text-[#374151]">
                          {formatValue(field, newV)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              <p className="text-[11px] text-gray-400 mt-1.5">
                {log.user_name || log.user?.full_name || log.user?.email || "System"}
                <span className="mx-1.5">·</span>
                {absoluteTime(log.created_at)}
              </p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
